import React from 'react';
import { Card } from 'antd';
import { useTranslation } from '../../../hooks/useTranslation';
import ScheduledQuickActions from './ScheduledQuickActions';
import ConfirmedQuickActions from './ConfirmedQuickActions';
import InProgressQuickActions from './InProgressQuickActions';
import ConsultedQuickActions from './ConsultedQuickActions';
import PendingQuickActions from './PendingQuickActions';
import CompletedQuickActions from './CompletedQuickActions';
import CancelledQuickActions from './CancelledQuickActions';
import NoShowQuickActions from './NoShowQuickActions';

export interface AppointmentQuickActionsProps {
  appointment: {
    id: string;
    status: string;
    provider_id?: string | null;
    patient_phone?: string | null;
    app_user?: {
      phone_number?: string | null;
    } | null;
    patient?: {
      phone_number?: string | null;
    } | null;
  };
  loadingStates: {
    starting?: boolean;
    updating?: boolean;
    completing?: boolean;
    cancelling?: boolean;
    markingNoShow?: boolean;
    assigningProvider?: boolean;
  };
  handlers: {
    onStartVideoCall: () => void;
    onMarkConsulted: () => void;
    onCompleteAppointment: () => void;
    onCancelAppointment: () => void;
    onMarkNoShow?: () => void;
    onAssignProvider?: () => void;
    onContactPatient?: (phoneNumber: string) => void;
  };
  showNoShowButton?: boolean;
  showAssignProviderButton?: boolean;
  showContactPatientButton?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const AppointmentQuickActions: React.FC<AppointmentQuickActionsProps> = ({
  appointment,
  loadingStates,
  handlers,
  showNoShowButton = false,
  showAssignProviderButton = true,
  showContactPatientButton = true,
  className,
  style
}) => {
  const { t } = useTranslation();

  // Render the appropriate component based on appointment status
  const renderQuickActions = () => {
    switch (appointment.status) {
      case 'scheduled':
        return (
          <ScheduledQuickActions
            appointment={appointment}
            loadingStates={loadingStates}
            handlers={handlers}
            showNoShowButton={showNoShowButton}
          />
        );
      
      case 'confirmed':
        return (
          <ConfirmedQuickActions
            appointment={appointment}
            loadingStates={loadingStates}
            handlers={handlers}
            showNoShowButton={showNoShowButton}
          />
        );
      
      case 'in_progress':
        return (
          <InProgressQuickActions
            appointment={appointment}
            loadingStates={loadingStates}
            handlers={handlers}
          />
        );
      
      case 'consulted':
        return (
          <ConsultedQuickActions
            appointment={appointment}
            loadingStates={loadingStates}
            handlers={handlers}
          />
        );
      
      case 'pending':
        return (
          <PendingQuickActions
            appointment={appointment}
            loadingStates={loadingStates}
            handlers={handlers}
            showNoShowButton={showNoShowButton}
            showAssignProviderButton={showAssignProviderButton}
            showContactPatientButton={showContactPatientButton}
          />
        );
      
      case 'completed':
        return (
          <CompletedQuickActions
            appointment={appointment}
          />
        );
      
      case 'cancelled':
        return (
          <CancelledQuickActions
            appointment={appointment}
          />
        );
      
      case 'no_show':
        return (
          <NoShowQuickActions
            appointment={appointment}
          />
        );
      
      default:
        // Fallback for unknown statuses - show basic actions
        return (
          <ScheduledQuickActions
            appointment={appointment}
            loadingStates={loadingStates}
            handlers={handlers}
            showNoShowButton={showNoShowButton}
          />
        );
    }
  };

  return (
    <Card 
      title={t('appointments.quickActions')} 
      className={className}
      style={style}
    >
      {renderQuickActions()}
    </Card>
  );
};

export default AppointmentQuickActions;
